import React from 'react';
import PropTypes from 'prop-types';

/**
 * LoadingScreen Component
 * 
 * Full-screen loading state with spinner and message.
 * Used during data fetching, authentication, etc.
 * 
 * Usage:
 * <LoadingScreen message="Wird geladen..." />
 * <LoadingScreen icon="📚" message="Kurse werden geladen" />
 */
export const LoadingScreen = ({
  message = 'Wird geladen...',
  icon = null,
  background = 'light', // 'light' = gray-50, 'dark' = gray-100
  size = 'md', // 'sm', 'md', 'lg'
  className = ''
}) => {
  const bgClasses = {
    light: 'bg-gray-50 dark:bg-gray-900',
    dark: 'bg-gray-100 dark:bg-gray-900'
  };

  const sizeClasses = {
    sm: { spinner: 'h-6 w-6', text: 'text-sm', icon: 'text-3xl' },
    md: { spinner: 'h-8 w-8', text: 'text-base', icon: 'text-5xl' },
    lg: { spinner: 'h-12 w-12', text: 'text-lg', icon: 'text-6xl' }
  };

  const sizes = sizeClasses[size];

  return (
    <div className={`min-h-screen ${bgClasses[background]} flex items-center justify-center theme-transition ${className}`}>
      <div className="text-center">
        {icon ? (
          <div className={`${sizes.icon} mb-4 animate-bounce`}>{icon}</div>
        ) : (
          <div className={`${sizes.spinner} border-4 border-primary-200 dark:border-primary-800 border-t-primary-500 rounded-full animate-spin mx-auto mb-4`}></div>
        )}
        <p className={`${sizes.text} text-gray-600 dark:text-gray-300`}>{message}</p>
      </div>
    </div>
  );
};

LoadingScreen.propTypes = {
  message: PropTypes.string,
  icon: PropTypes.string,
  background: PropTypes.oneOf(['light', 'dark']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * LoadingSpinner Component
 * 
 * Inline spinner for buttons, cards, etc.
 * 
 * Usage:
 * <LoadingSpinner size="sm" />
 */
export const LoadingSpinner = ({
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-2',
    lg: 'h-8 w-8 border-4'
  };

  const colorClasses = {
    primary: 'border-primary-200 dark:border-primary-800 border-t-primary-500',
    white: 'border-white/30 border-t-white',
    gray: 'border-gray-200 dark:border-gray-700 border-t-gray-500'
  };

  return (
    <div className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-spin ${className}`}></div>
  );
};

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  color: PropTypes.oneOf(['primary', 'white', 'gray']),
  className: PropTypes.string
};

export default LoadingScreen;
